import { onMounted, onBeforeUnmount, Ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { useVirtualMblStore } from '@/store/modules/master-bl/useVirtualMblStore';
import { useVirtualHblStore } from '@/store/modules/house-bl/useVirtualHblStore';
import { useToast } from '@/composables/useToast';

declare global {
  interface Window {
    activeInventoryInputIndex: Ref<number | null>;
  }
}

export function useBarcodeScanner() {
  const router = useRouter();
  const toast = useToast();
  const virtualMblStore = useVirtualMblStore();
  const virtualHblStore = useVirtualHblStore();

  let barcodeBuffer = '';
  let barcodeTimeout: ReturnType<typeof setTimeout> | null = null;

  const handleBarcodeScan = async (barcode: string) => {
    const code = barcode.trim();

    const prevMblFilters = { ...virtualMblStore.filterForm.value };
    virtualMblStore.filterForm.value = {
      ...virtualMblStore.filterForm.value,
      shipmentNumber: code,
    };
    await virtualMblStore.search();
    const foundMbl = virtualMblStore.items?.find((item) => item.shipmentNumber === code);

    if (foundMbl && foundMbl._id) {
      router.push({ name: 'MasterBL', params: { id: foundMbl._id } });
      console.log('Scanned MBL barcode:', code, 'Found _id:', foundMbl._id);
      virtualMblStore.filterForm.value = prevMblFilters;
      return;
    }
    virtualMblStore.filterForm.value = prevMblFilters;

    const prevHblFilters = { ...virtualHblStore.filterForm.value };
    virtualHblStore.filterForm.value = {
      ...virtualHblStore.filterForm.value,
      shipmentNumber: code,
    };
    await virtualHblStore.search();
    const foundHbl = virtualHblStore.items?.find((item) => item.shipmentNumber === code);

    if (foundHbl && foundHbl._id) {
      router.push({ name: 'HouseBL', params: { id: foundHbl._id } });
      console.log('Scanned HBL barcode:', code, 'Found _id:', foundHbl._id);
      virtualHblStore.filterForm.value = prevHblFilters;
      return;
    }
    virtualHblStore.filterForm.value = prevHblFilters;

    toast.error('MBL/HBL not found: ' + code);
  };

  const keydownListener = async (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      barcodeBuffer = '';
      return;
    }
    if (e.key === 'Shift' || e.key === 'Alt' || e.key === 'Control') return;

    if (barcodeTimeout) clearTimeout(barcodeTimeout);
    barcodeTimeout = setTimeout(() => {
      barcodeBuffer = '';
    }, 200);

    if (e.key === 'Enter') {
      if (barcodeBuffer.length > 0) {
        if (window.activeInventoryInputIndex && window.activeInventoryInputIndex.value !== null) {
          window.dispatchEvent(
            new CustomEvent('inventory-location-scan', { detail: { barcode: barcodeBuffer } })
          );
          barcodeBuffer = '';
          return;
        }
        await handleBarcodeScan(barcodeBuffer);
        barcodeBuffer = '';
      }
    } else {
      barcodeBuffer += e.key;
    }
  };

  onMounted(() => {
    window.addEventListener('keydown', keydownListener);
  });
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', keydownListener);
  });
}
